import React, { useEffect, useState } from "react";
import { Coins, IndianRupee, Eye, BookOpen, Download, ArrowRightLeft, Loader2, Undo2 } from "lucide-react";
import { AdminShell } from "@/components/admin/AdminShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";

const grams = (g) => `${(Number(g) || 0).toFixed(3)} g`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

const goldBalanceClass = (v) => {
  if (v > 0.0005) return "text-[hsl(var(--gold-dark))]";
  if (v < -0.0005) return "text-[#7A1F1F]";
  return "text-muted-foreground";
};
const inrBalanceClass = (v) => {
  if (v > 0.5) return "text-[#7A1F1F]";
  if (v < -0.5) return "text-[#1F5B2E]";
  return "text-muted-foreground";
};

function StatementTable({ entries, unit, canUndo, onUndo, undoingRef }) {
  if (!entries || entries.length === 0) {
    return <div className="py-12 text-center text-sm text-muted-foreground">No {unit === "gold" ? "gold" : "rupee"} transactions yet.</div>;
  }
  const fmt = unit === "gold" ? grams : formatINR;
  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-28">Date</TableHead>
            <TableHead>Description</TableHead>
            <TableHead className="text-right">Debit</TableHead>
            <TableHead className="text-right">Credit</TableHead>
            <TableHead className="text-right">Balance</TableHead>
            {canUndo && <TableHead className="text-right">Actions</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.map((e) => (
            <TableRow key={e.id} data-testid="partner-ledger-row" className={e.reversed ? "opacity-60" : ""}>
              <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(e.date)}</TableCell>
              <TableCell className="text-sm">
                <div>{e.description}</div>
                <div className="flex items-center gap-1.5 mt-0.5">
                  {e.ref_no && <span className="text-[11px] text-muted-foreground">{e.ref_no}</span>}
                  {e.reversed && <Badge variant="outline" className="text-[10px] py-0">Reversed</Badge>}
                </div>
              </TableCell>
              <TableCell className="text-right font-mono text-sm text-[#7A1F1F]">{e.debit ? fmt(e.debit) : "—"}</TableCell>
              <TableCell className="text-right font-mono text-sm text-[#1F5B2E]">{e.credit ? fmt(e.credit) : "—"}</TableCell>
              <TableCell className="text-right font-mono text-sm font-medium">{fmt(e.balance)}</TableCell>
              {canUndo && (
                <TableCell className="text-right">
                  {e.entry_type === "conversion" && !e.reversed ? (
                    <Button data-testid="undo-conversion-button" size="sm" variant="ghost" className="gap-1 h-7 text-xs"
                      disabled={undoingRef === e.ref_no} onClick={() => onUndo(e)}>
                      {undoingRef === e.ref_no ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Undo2 className="h-3.5 w-3.5" />} Undo
                    </Button>
                  ) : null}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// Renders the partner balances table: skeleton while loading, empty-state message, or the data table.
function PartnersTable({ loading, partners, onView, onDownload }) {
  if (loading) {
    return <div className="p-5 space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>;
  }
  if (partners.length === 0) {
    return <div className="py-16 text-center text-muted-foreground">No approved partners yet.</div>;
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Partner</TableHead>
          <TableHead>Salesman</TableHead>
          <TableHead className="text-right">Gold Balance (fine)</TableHead>
          <TableHead className="text-right">Rupee Balance</TableHead>
          <TableHead className="text-right">Statement</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {partners.map((p) => (
          <TableRow key={p.shop_id} data-testid="partner-summary-row">
            <TableCell>
              <div className="font-medium">{p.shop_name}</div>
              <div className="text-xs text-muted-foreground">{p.phone}</div>
            </TableCell>
            <TableCell className="text-sm text-muted-foreground">{p.salesman_name || "—"}</TableCell>
            <TableCell className={`text-right font-mono font-medium ${goldBalanceClass(p.gold_balance_fine_g)}`}>{grams(p.gold_balance_fine_g)}</TableCell>
            <TableCell className={`text-right font-mono font-medium ${inrBalanceClass(p.inr_balance)}`}>{formatINR(p.inr_balance)}</TableCell>
            <TableCell className="text-right">
              <div className="flex items-center justify-end gap-2">
                <Button data-testid="partner-view-statement" variant="outline" size="sm" className="gap-1.5" onClick={() => onView(p)}><Eye className="h-3.5 w-3.5" /> View</Button>
                <Button data-testid="partner-download-pdf" variant="ghost" size="sm" className="gap-1.5" onClick={() => onDownload(p)}><Download className="h-3.5 w-3.5" /> PDF</Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export default function PartnerLedger() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [openFor, setOpenFor] = useState(null);
  const [goldRate, setGoldRate] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [convDir, setConvDir] = useState(null);      // 'rupee_to_gold' | 'gold_to_rupee' | null
  const [convAmount, setConvAmount] = useState("");
  const [converting, setConverting] = useState(false);
  const [staff, setStaff] = useState(null);
  const [adjOpen, setAdjOpen] = useState(false);
  const [adjForm, setAdjForm] = useState({ kind: "inr", amount: "", note: "" });
  const [undoingRef, setUndoingRef] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/crm/partners/ledger-summary");
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load partner ledgers");
    } finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    api.get("/rates/public").then(({ data }) => setGoldRate(data.gold_rate_per_gram_24k || 0)).catch(() => {});
    api.get("/crm/me").then(({ data }) => setStaff(data)).catch(() => {});
  }, []);

  const openStatement = async (p) => {
    setOpenFor(p); setDetail(null); setDetailLoading(true); setConvDir(null); setConvAmount("");
    try {
      const { data } = await api.get(`/crm/partners/${p.shop_id}/ledger`);
      setDetail(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load statement");
    } finally { setDetailLoading(false); }
  };

  const downloadPdf = async (p) => {
    setDownloading(true);
    try {
      const res = await api.get(`/crm/partners/${p.shop_id}/ledger/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Partner-Statement-${(p.shop_name || "Partner").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Failed to download statement");
    } finally { setDownloading(false); }
  };

  const startConvert = (dir) => {
    setConvDir(dir);
    // One-click: prefill with the full outstanding balance in the source unit.
    if (dir === "rupee_to_gold") setConvAmount(detail?.inr_balance > 0 ? String(Math.round(detail.inr_balance)) : "");
    else setConvAmount(detail?.gold_balance_fine_g > 0 ? String(detail.gold_balance_fine_g) : "");
  };

  const convPreview = (() => {
    const amt = Number(convAmount) || 0;
    if (!amt || !goldRate) return null;
    return convDir === "rupee_to_gold" ? `${(amt / goldRate).toFixed(3)} g` : formatINR(amt * goldRate);
  })();

  const submitConvert = async () => {
    const amt = Number(convAmount) || 0;
    if (amt <= 0) { toast.error("Enter an amount to convert"); return; }
    setConverting(true);
    try {
      const { data } = await api.post(`/crm/partners/${openFor.shop_id}/convert`, {
        direction: convDir, amount: amt,
      });
      setDetail(data.ledger);
      setConvDir(null); setConvAmount("");
      toast.success(`Converted at ${formatINR(data.rate_per_gram_24k)}/g`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Conversion failed");
    } finally { setConverting(false); }
  };

  const partners = data?.partners || [];

  const undoConversion = async (entry) => {
    setUndoingRef(entry.ref_no);
    try {
      const { data } = await api.post(`/crm/partners/${openFor.shop_id}/convert/${entry.ref_no}/undo`);
      setDetail(data.ledger);
      toast.success(`Conversion ${entry.ref_no} reversed`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to undo conversion"); }
    finally { setUndoingRef(null); }
  };

  const openAdjustment = () => { setAdjForm({ kind: "inr", amount: "", note: "" }); setAdjOpen(true); };
  const saveAdjustment = async () => {
    if (!adjForm.amount) { toast.error("Enter an amount"); return; }
    if (!adjForm.note || !adjForm.note.trim()) { toast.error("A reason is required for every manual adjustment"); return; }
    try {
      const { data } = await api.post(`/crm/partners/${openFor.shop_id}/adjustments`, {
        amount: Number(adjForm.amount), kind: adjForm.kind, note: adjForm.note.trim(),
      });
      setDetail(data.ledger);
      toast.success("Adjustment recorded"); setAdjOpen(false); setAdjForm({ kind: "inr", amount: "", note: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to add adjustment"); }
  };

  return (
    <AdminShell title="Partner Ledger">
      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card className="p-5">
          <div className="flex items-center gap-2 text-muted-foreground text-sm"><BookOpen className="h-4 w-4" /> Partners</div>
          <div data-testid="pl-total-partners" className="text-2xl font-semibold mt-1">{loading ? "—" : partners.length}</div>
        </Card>
        <Card className="p-5">
          <div className="flex items-center gap-2 text-muted-foreground text-sm"><Coins className="h-4 w-4 text-[hsl(var(--primary))]" /> Total Gold Receivable</div>
          <div data-testid="pl-total-gold" className={`text-2xl font-semibold mt-1 font-mono ${goldBalanceClass(data?.total_gold_fine_g || 0)}`}>{loading ? "—" : grams(data?.total_gold_fine_g)}</div>
          <div className="text-xs text-muted-foreground">fine (24K)</div>
        </Card>
        <Card className="p-5">
          <div className="flex items-center gap-2 text-muted-foreground text-sm"><IndianRupee className="h-4 w-4 text-[hsl(var(--primary))]" /> Total Rupee Receivable</div>
          <div data-testid="pl-total-inr" className={`text-2xl font-semibold mt-1 font-mono ${inrBalanceClass(data?.total_inr || 0)}`}>{loading ? "—" : formatINR(data?.total_inr)}</div>
        </Card>
      </div>

      <Card className="p-0 overflow-hidden">
        <div className="px-5 py-4 border-b border-border">
          <h2 className="font-display text-lg">Running Balances by Partner</h2>
          <p className="text-xs text-muted-foreground">A positive balance means the partner still owes you.</p>
        </div>
        <PartnersTable loading={loading} partners={partners} onView={openStatement} onDownload={downloadPdf} />
      </Card>

      {/* Statement dialog */}
      <Dialog open={!!openFor} onOpenChange={(o) => !o && setOpenFor(null)}>
        <DialogContent data-testid="partner-statement-dialog" className="max-w-3xl">
          <DialogHeader><DialogTitle className="flex items-center justify-between pr-6 flex-wrap gap-2">
            <span>Statement — {openFor?.shop_name}</span>
            {staff?.is_manager && (
              <Button data-testid="partner-open-adjustment" size="sm" variant="outline" className="gap-1" onClick={openAdjustment}>+/− New Adjustment</Button>
            )}
          </DialogTitle></DialogHeader>
          {detailLoading ? (
            <div className="space-y-3 py-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : detail ? (
            <>
              {/* Settle / Convert at today's rate */}
              <div className="rounded-lg border border-border bg-[#FBF6EE] p-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 text-sm font-medium"><ArrowRightLeft className="h-4 w-4 text-[hsl(var(--primary))]" /> Settle / Convert</div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Today&apos;s 24K rate: <span className="font-mono text-foreground">{goldRate ? formatINR(goldRate) : "—"}/g</span></span>
                    <Button data-testid="partner-pdf-in-dialog" variant="outline" size="sm" className="gap-1.5" disabled={downloading} onClick={() => downloadPdf(openFor)}>
                      {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} PDF
                    </Button>
                  </div>
                </div>
                {!convDir ? (
                  <div className="flex flex-wrap gap-2 mt-3">
                    <Button data-testid="settle-rupee-in-gold" size="sm" variant="secondary" className="gap-1.5" disabled={!goldRate} onClick={() => startConvert("rupee_to_gold")}>
                      <IndianRupee className="h-3.5 w-3.5" /> Settle Rupee in Gold
                    </Button>
                    <Button data-testid="settle-gold-in-rupee" size="sm" variant="secondary" className="gap-1.5" disabled={!goldRate} onClick={() => startConvert("gold_to_rupee")}>
                      <Coins className="h-3.5 w-3.5" /> Settle Gold in Rupee
                    </Button>
                  </div>
                ) : (
                  <div className="mt-3 flex flex-wrap items-end gap-3">
                    <div className="flex-1 min-w-[160px]">
                      <Label className="text-xs">{convDir === "rupee_to_gold" ? "Rupee amount (₹)" : "Gold weight (fine g)"}</Label>
                      <Input data-testid="convert-amount-input" type="number" value={convAmount} onChange={(e) => setConvAmount(e.target.value)} className="mt-1 font-mono" placeholder={convDir === "rupee_to_gold" ? "0" : "0.000"} />
                    </div>
                    <div className="text-sm pb-2">
                      <span className="text-muted-foreground">→ </span>
                      <span data-testid="convert-preview" className="font-mono font-semibold text-[hsl(var(--gold-dark))]">{convPreview || "—"}</span>
                    </div>
                    <div className="flex gap-2 pb-1">
                      <Button data-testid="convert-cancel" size="sm" variant="ghost" onClick={() => { setConvDir(null); setConvAmount(""); }}>Cancel</Button>
                      <Button data-testid="convert-submit" size="sm" disabled={converting || !convAmount} onClick={submitConvert}>
                        {converting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null} Convert
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <Tabs defaultValue="gold" className="mt-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <TabsList>
                    <TabsTrigger data-testid="statement-tab-gold" value="gold" className="gap-1.5"><Coins className="h-3.5 w-3.5" /> Gold</TabsTrigger>
                    <TabsTrigger data-testid="statement-tab-inr" value="inr" className="gap-1.5"><IndianRupee className="h-3.5 w-3.5" /> Rupee</TabsTrigger>
                  </TabsList>
                  <div className="flex gap-2">
                    <Badge className={`bg-[hsl(var(--gold-light))] border border-border ${goldBalanceClass(detail.gold_balance_fine_g)}`}>Gold: {grams(detail.gold_balance_fine_g)}</Badge>
                    <Badge className={`bg-[#F7F0E6] border border-border ${inrBalanceClass(detail.inr_balance)}`}>Rupee: {formatINR(detail.inr_balance)}</Badge>
                  </div>
                </div>
                <TabsContent value="gold" className="mt-4 max-h-[45vh] overflow-y-auto">
                  <StatementTable entries={detail.gold_entries} unit="gold" canUndo={staff?.is_manager} onUndo={undoConversion} undoingRef={undoingRef} />
                  <div className="flex justify-end gap-6 text-sm mt-3 pr-2">
                    <span className="text-muted-foreground">Debited <span className="font-mono text-foreground">{grams(detail.gold_total_debit)}</span></span>
                    <span className="text-muted-foreground">Credited <span className="font-mono text-foreground">{grams(detail.gold_total_credit)}</span></span>
                    <span className="font-medium">Balance <span className={`font-mono ${goldBalanceClass(detail.gold_balance_fine_g)}`}>{grams(detail.gold_balance_fine_g)}</span></span>
                  </div>
                </TabsContent>
                <TabsContent value="inr" className="mt-4 max-h-[45vh] overflow-y-auto">
                  <StatementTable entries={detail.inr_entries} unit="inr" canUndo={staff?.is_manager} onUndo={undoConversion} undoingRef={undoingRef} />
                  <div className="flex justify-end gap-6 text-sm mt-3 pr-2">
                    <span className="text-muted-foreground">Debited <span className="font-mono text-foreground">{formatINR(detail.inr_total_debit)}</span></span>
                    <span className="text-muted-foreground">Credited <span className="font-mono text-foreground">{formatINR(detail.inr_total_credit)}</span></span>
                    <span className="font-medium">Balance <span className={`font-mono ${inrBalanceClass(detail.inr_balance)}`}>{formatINR(detail.inr_balance)}</span></span>
                  </div>
                </TabsContent>
              </Tabs>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Manual Ledger Adjustment — bill correction / audit-trail tool */}
      <Dialog open={adjOpen} onOpenChange={setAdjOpen}>
        <DialogContent data-testid="partner-adjustment-dialog" className="max-w-sm">
          <DialogHeader>
            <DialogTitle>New Ledger Adjustment</DialogTitle>
            <DialogDescription>Positive amount increases what the partner owes (debit); negative reduces it (credit) — e.g. settling a bill for a deleted invoice.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Currency</Label>
              <Select value={adjForm.kind} onValueChange={(v) => setAdjForm({ ...adjForm, kind: v })}>
                <SelectTrigger data-testid="partner-adjustment-kind-select" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="inr">Rupee (₹)</SelectItem><SelectItem value="gold">Gold (fine g)</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Amount {adjForm.kind === "gold" ? "(fine g)" : "(₹)"}</Label>
              <Input data-testid="partner-adjustment-amount-input" type="number" value={adjForm.amount} onChange={(e) => setAdjForm({ ...adjForm, amount: e.target.value })} className="mt-1 font-mono" placeholder="e.g. -500 for a credit" />
            </div>
            <div>
              <Label className="text-xs">Reason (required)</Label>
              <Textarea data-testid="partner-adjustment-note-input" value={adjForm.note} onChange={(e) => setAdjForm({ ...adjForm, note: e.target.value })} rows={2} className="mt-1" placeholder="Reason for adjustment" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjOpen(false)}>Cancel</Button>
            <Button data-testid="partner-adjustment-save" disabled={!adjForm.amount || !adjForm.note.trim()} onClick={saveAdjustment}>Save Adjustment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminShell>
  );
}
